import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Copy, Expand, RotateCw, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BACKEND, errMsg, fmtTime } from "@/lib/api";
import { card, Empty, inputCls } from "@/components/admin/kit";
import { StatusBadge, useGet } from "./common";
import { FirstCallPreview } from "./FirstCallPreview";
import { CaseLines, lineProblem, listPurpose } from "./CaseLines";
import { SendBackDialog } from "./Queue";

function useRotated(src, r) {
  const [out, setOut] = useState(src);
  useEffect(() => {
    if (!r) { setOut(src); return undefined; }
    let live = true;
    const img = new Image();
    img.onload = () => {
      const side = r % 180 !== 0;
      const c = document.createElement("canvas");
      c.width = side ? img.naturalHeight : img.naturalWidth;
      c.height = side ? img.naturalWidth : img.naturalHeight;
      const x = c.getContext("2d");
      x.translate(c.width / 2, c.height / 2);
      x.rotate((r * Math.PI) / 180);
      x.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      if (live) setOut(c.toDataURL("image/jpeg", 0.92));
    };
    img.src = src;
    return () => { live = false; };
  }, [src, r]);
  return out;
}

function Photos({ urls }) {
  const [i, setI] = useState(0);
  const [z, setZ] = useState(1);
  const [r, setR] = useState(0);
  const src = `${BACKEND}${urls[i]}`;
  const shown = useRotated(src, r);
  return (
    <div className={`${card} grid content-start gap-2`} data-testid="case-photos">
      <div className="flex flex-wrap gap-1">
        {urls.map((_, j) => <Button key={j} size="sm" variant={j === i ? "default" : "outline"} onClick={() => { setI(j); setZ(1); setR(0); }} data-testid={`case-photo-tab-${j}`}>Photo {j + 1}</Button>)}
        <div className="ml-auto flex gap-1">
          <Button size="icon" variant="outline" onClick={() => setZ(Math.min(4, z + 0.5))} aria-label="Zoom in" data-testid="photo-zoom-in"><ZoomIn size={14} /></Button>
          <Button size="icon" variant="outline" onClick={() => setZ(Math.max(1, z - 0.5))} aria-label="Zoom out" data-testid="photo-zoom-out"><ZoomOut size={14} /></Button>
          <Button size="icon" variant="outline" onClick={() => setR((r + 90) % 360)} aria-label="Rotate" data-testid="photo-rotate"><RotateCw size={14} /></Button>
          <a href={src} target="_blank" rel="noreferrer" className="inline-flex h-9 w-9 items-center justify-center rounded-md border" aria-label="Full size" data-testid="photo-full"><Expand size={14} /></a>
        </div>
      </div>
      <div className="h-[420px] overflow-auto rounded-2xl bg-[var(--lav)]">
        <img src={shown} alt={`Prescription photo ${i + 1}`} className="mx-auto block max-w-none" style={{ width: `${z * 100}%`, height: "auto" }} data-testid="case-photo-img" data-rotation={r} />
      </div>
    </div>
  );
}

function Header({ d }) {
  const p = d.patient;
  return (
    <div className={`${card} grid gap-1 text-sm`} data-testid="case-header">
      <div className="flex flex-wrap items-center gap-2"><p className="text-xl font-bold" data-testid="case-number">{d.case_no}</p><StatusBadge s={d.status} testid="case-status" />{d.dup_of && <span className="text-xs font-semibold text-purple-700" data-testid="case-dup-of">duplicate of {d.dup_of}</span>}</div>
      <p><b data-testid="case-org">{d.org_name}</b> <span className="text-xs text-muted-foreground">(locked)</span>{d.doctor ? ` · ${d.doctor.name}` : ""} · by {d.submitted_by?.name} · {fmtTime(d.created_at)}</p>
      <p data-testid="case-patient"><b>{p.name}</b>, {p.age}, {p.gender} · {p.phone_masked} · {d.photo_type === "pharmacy_bill" ? "pharmacy bill" : "prescription"}</p>
      <p>Calls go to: {p.receiver === "caregiver" ? `${p.family_name} (${p.relationship})` : "the patient"} · Consent: {d.consent?.by}, {fmtTime(d.consent?.at)}</p>
      {d.letterhead_flag && <p className="mt-1 flex items-center gap-2 rounded-xl bg-red-600 px-3 py-2 font-bold text-white" data-testid="case-letterhead-flag"><AlertTriangle size={16} /> {d.letterhead_flag}</p>}
      {d.send_back && <p className="text-red-700">Sent back: {d.send_back.reason} {d.send_back.note}</p>}
      {d.ai?.status === "failed" && <p className="text-red-700" data-testid="case-ai-failed">AI could not read the photo: {d.ai.error}</p>}
      {d.ai?.doctor_name && <p className="text-xs text-muted-foreground">Doctor name as read by AI: {d.ai.doctor_name}</p>}
      {(d.ai?.warnings || []).map((w, i) => <p key={i} className="rounded-xl bg-amber-100 px-3 py-2 font-semibold text-amber-900" data-testid={`case-ai-warning-${i}`}><AlertTriangle size={14} className="mr-1 inline" />{w}</p>)}
    </div>
  );
}

function Appointment({ a, setA }) {
  if (!a) return <Button variant="outline" onClick={() => setA({ type: "followup", date: "", time_mode: "morning", time: "", instruction: "", treatment: "", sitting: 1 })} data-testid="case-add-appt">Add follow-up / dental sitting</Button>;
  const up = (k) => (e) => setA({ ...a, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  return (
    <div className="grid gap-2 rounded-2xl border p-3 sm:grid-cols-3" data-testid="case-appt">
      <select className={inputCls} value={a.type} onChange={up("type")} data-testid="appt-type"><option value="followup">Follow-up</option><option value="dental">Dental sitting</option></select>
      <input type="date" className={inputCls} value={a.date} onChange={up("date")} data-testid="appt-date" />
      <select className={inputCls} value={a.time_mode} onChange={up("time_mode")} data-testid="appt-time-mode"><option value="morning">Morning</option><option value="evening">Evening</option><option value="exact">Exact time</option></select>
      {a.time_mode === "exact" && <input type="time" className={inputCls} value={a.time} onChange={up("time")} data-testid="appt-time" />}
      {a.type === "dental" ? <><input className={inputCls} placeholder="Treatment (e.g. root canal)" value={a.treatment} onChange={up("treatment")} data-testid="appt-treatment" /><input type="number" min={1} max={10} className={inputCls} value={a.sitting} onChange={up("sitting")} data-testid="appt-sitting" /></>
        : <input className={inputCls} placeholder="Instruction (e.g. come fasting)" value={a.instruction} onChange={up("instruction")} data-testid="appt-instruction" />}
      <Button variant="ghost" onClick={() => setA(null)} data-testid="appt-remove">Remove</Button>
    </div>
  );
}

function Notes({ api, d }) {
  const [t, setT] = useState("");
  const [notes, setNotes] = useState(d.notes || []);
  const add = async () => { try { const r = await api.post(`/subs/${d.id}/notes`, { text: t }); setNotes([...notes, r.data]); setT(""); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={`${card} grid gap-2 text-sm`} data-testid="case-notes">
      <p className="font-bold">Internal notes</p>
      {notes.map((n, i) => <p key={i} className="border-b border-[var(--lav-2)] pb-1"><b>{n.by}</b> · {fmtTime(n.at)}: {n.text}</p>)}
      <div className="flex gap-2"><input className={inputCls} value={t} onChange={(e) => setT(e.target.value)} placeholder="Add a note" data-testid="case-note-input" /><Button onClick={add} disabled={!t.trim()} data-testid="case-note-add">Add</Button></div>
    </div>
  );
}

export function CaseView({ api, base }) {
  const { id } = useParams();
  const [d, err, reload] = useGet(api, `/subs/${id}`);
  const [catalog] = useGet(api, "/subs/catalog/purposes");
  const [lines, setLines] = useState([]);
  const [appt, setAppt] = useState(null);
  const [nv, setNv] = useState("");
  const [same, setSame] = useState("");
  const [busy, setBusy] = useState(false);
  const [sb, setSb] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [voices] = useGet(api, "/subs/catalog/voices");
  const [saved, setSaved] = useState("");
  const skip = useRef(true);
  useEffect(() => { if (d) { skip.current = true; setLines(d.lines.map((l, i) => ({ ...l, _k: `${d.id}-${i}-${Math.random()}` }))); setAppt(d.appointment); setNv(d.patient.name_voice || ""); } }, [d]);
  useEffect(() => {
    if (!d || d.status !== "ready") { return; }
    if (skip.current) { skip.current = false; return; }
    setSaved("saving");
    const t = setTimeout(async () => {
      try { await api.put(`/subs/${d.id}/draft`, { lines: lines.filter((l) => l.name.trim()), appointment: appt, patient_name_voice: nv }); setSaved("saved"); }
      catch (e) { setSaved("error"); }
    }, 700);
    return () => clearTimeout(t);
  }, [lines, appt, nv]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (d && ["received", "ai_reading"].includes(d.status)) { const t = setTimeout(reload, 4000); return () => clearTimeout(t); }
  }, [d, reload]);
  if (err) return <div className={card}><p className="font-bold text-red-700" data-testid="case-error">{err}</p></div>;
  if (!d) return <Empty>Loading…</Empty>;
  const open = ["received", "ai_reading", "ready"].includes(d.status);
  const allTicked = lines.every((l) => l.ticked && !lineProblem(l, listPurpose(catalog, l.name))) && (lines.length > 0 || appt);
  const body = { lines, appointment: appt, patient_name_voice: nv };
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const pron = async (ln) => {
    try { await api.post("/subs/pron/add", { written: ln.name, spoken: ln.name_voice }); setLines(lines.map((x) => (x === ln ? { ...x, in_pron_list: true } : x))); toast.success(`${ln.name} added to pronunciation list`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const play = async (i, text) => {
    setPlaying(i);
    try { const r = await api.post("/subs/say", { text }, { responseType: "blob" }); const a = new Audio(URL.createObjectURL(r.data)); a.onended = () => setPlaying(null); await a.play(); }
    catch (e) { toast.error("Could not play the name"); setPlaying(null); }
  };
  const stuck = d.ai?.status === "failed" || (d.ai?.status === "reading" && d.ai.started_at && Date.now() - new Date(d.ai.started_at).getTime() > 180000);
  return (
    <div className="grid gap-4" data-testid="case-view">
      <Link to={base} className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--violet)]" data-testid="case-back"><ArrowLeft size={14} /> Submissions</Link>
      <Header d={d} />
      <div className="grid gap-4 xl:grid-cols-[1fr_1.3fr]">
        <Photos urls={d.photos} />
        <div className={`${card} grid content-start gap-3`}>
          <p className="font-bold">Medicine lines {["received", "ai_reading"].includes(d.status) && <span className="text-xs font-normal text-sky-700" data-testid="case-ai-reading">AI is reading the photo…</span>}
            {saved && <span className={`ml-2 text-xs font-semibold ${saved === "error" ? "text-red-700" : saved === "saved" ? "text-green-700" : "text-muted-foreground"}`} data-testid="case-autosave-status">{saved === "saved" ? "Saved" : saved === "saving" ? "Saving…" : "Not saved — check connection"}</span>}</p>
          <p className="text-xs text-muted-foreground">Yellow = AI unsure. Tick every line after checking it against the photo. Shorthand box understands 1-0-1, OD, BD, TDS, QID, HS, AC, PC, x 5 days, x 1 month.</p>
          <fieldset disabled={d.status !== "ready"} className="grid gap-3">
          <CaseLines lines={lines} setLines={setLines} onPron={pron} canPron={d.me?.role === "super_admin"} catalog={catalog} voices={voices} onPlay={play} playing={playing} />
          <Appointment a={appt} setA={setAppt} />
          <label className="grid gap-1 text-sm font-semibold">Patient name as spoken (Telugu, optional)<input className={`${inputCls} font-te`} value={nv} onChange={(e) => setNv(e.target.value)} data-testid="case-patient-voice" /></label>
          </fieldset>
          {open && d.status === "ready" && <FirstCallPreview api={api} sid={d.id} body={{ lines: lines.filter((l) => l.name.trim()), appointment: appt, patient_name_voice: nv }} />}
          {d.same_person.length > 0 && !d.approved && (
            <div className="rounded-2xl bg-amber-50 p-3 text-sm" data-testid="case-same-person">
              <p className="font-bold">This phone is already linked to:</p>
              {d.same_person.map((m) => <label key={m.patient_id} className="flex items-center gap-2"><input type="radio" name="same" checked={same === m.patient_id} onChange={() => setSame(m.patient_id)} data-testid={`same-yes-${m.patient_id}`} /> {m.label} — same person</label>)}
              <label className="flex items-center gap-2"><input type="radio" name="same" checked={same === ""} onChange={() => setSame("")} data-testid="same-no" /> No, new person</label>
            </div>
          )}
          {open && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              <Button disabled={busy || !allTicked} onClick={() => run(() => api.post(`/subs/${d.id}/approve`, { ...body, same_person: same || null }), "Approved — calls started")} className="bg-green-700 hover:bg-green-800" data-testid="case-approve-btn">Approve</Button>
              <Button variant="destructive" disabled={busy} onClick={() => setSb(true)} data-testid="case-sendback-btn">Send back</Button>
              <Button variant="outline" disabled={busy} onClick={() => { const ref = window.prompt(`${d.case_no} is a duplicate of which case?`, d.dup_suggest || ""); if (ref !== null) run(() => api.post(`/subs/${d.id}/duplicate`, { duplicate: true, dup_of: ref }), "Marked duplicate"); }} data-testid="case-dup-btn"><Copy size={14} /> Mark duplicate</Button>
              {stuck && <Button variant="outline" onClick={() => run(() => api.post(`/subs/${d.id}/reread`), "Reading again")} data-testid="case-reread-btn">Read again</Button>}
              {!allTicked && <p className="w-full text-xs text-muted-foreground" data-testid="case-approve-hint">Approve turns on when every line is ticked and has no red message (duration chosen, reason for any purpose change).</p>}
            </div>
          )}
          {d.status === "duplicate" && <Button variant="outline" onClick={() => run(() => api.post(`/subs/${d.id}/duplicate`, { duplicate: false }), "Back in the queue")} data-testid="case-not-dup-btn">Not a duplicate</Button>}
          {d.approved && <p className="rounded-xl bg-green-50 p-3 text-sm font-semibold text-green-800" data-testid="case-approved-info">Approved by {d.checker} · {fmtTime(d.checked_at)} · {d.approved.medicine_ids.length} medicines saved{d.approved.spoken_name && <span className="mt-1 block font-normal" data-testid="case-approved-spoken-name">Calls will say the patient's name as: <b>{d.approved.spoken_name}</b></span>}</p>}
        </div>
      </div>
      <Notes api={api} d={d} />
      {sb && <SendBackDialog api={api} item={d} onClose={() => setSb(false)} onDone={reload} />}
    </div>
  );
}
